
"use client";

import * as React from "react";
import type { Service } from "./BookingFlow";
import { formatDuration } from "@/components/booking-flow/FlowSteps";
import { FLOW_DEFAULTS } from "@/components/booking-flow/themes";

/**
 * ServiceSelectionStep — step 1 of the booking flow.
 *
 * Matched against the Claude Design prototype (screens.jsx ServiceCard
 * in design-imports/). The page header is rendered by BookingFlow's
 * scaffold; this component renders only the service card list.
 */

interface ServiceSelectionStepProps {
  selectedService: Service | null;
  onSelectService: (service: Service) => void;
  services?: Service[];
}

const DISPLAY_FONT: React.CSSProperties = {
  fontFamily: 'var(--f-display, "Inter Tight", system-ui, sans-serif)',
  fontStyle: 'var(--f-display-style, normal)' as React.CSSProperties['fontStyle'],
  fontWeight: 'var(--f-display-weight, 600)' as React.CSSProperties['fontWeight'],
};

function isFeatured(specialRequirements?: string): boolean {
  if (!specialRequirements) return false;
  const lower = specialRequirements.toLowerCase();
  return lower.includes("waitlist") || lower.includes("most booked");
}

function ServiceCard({ service, selected, onClick }: {
  service: Service;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      style={{
        textAlign: 'left',
        background: selected
          ? 'var(--f-accent-soft, rgba(0,0,0,0.04))'
          : 'var(--f-surface, #FBF8F2)',
        border: `0.5px solid ${selected ? 'var(--f-accent-border, rgba(28,18,12,0.20))' : 'var(--f-hairline, rgba(28,18,12,0.10))'}`,
        boxShadow: selected
          ? '0 0 0 1px var(--f-accent-border, rgba(28,18,12,0.20)), 0 8px 18px rgba(28,18,12,0.06)'
          : FLOW_DEFAULTS.cardShadow,
        padding: '18px 18px 20px',
        borderRadius: 18,
        cursor: 'pointer',
        fontFamily: 'inherit',
        color: 'var(--f-ink, #1c160f)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        width: '100%',
        outline: 'none',
        transition: 'background 240ms ease, border-color 240ms ease, box-shadow 240ms ease',
        WebkitTapHighlightColor: 'transparent',
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            ...DISPLAY_FONT,
            fontSize: 22,
            lineHeight: 1.1,
            letterSpacing: '-0.01em',
            color: 'var(--f-ink, #1c160f)',
          }}>{service.name}</div>
          {isFeatured(service.specialRequirements) && (
            <div style={{
              display: 'inline-block',
              marginTop: 6,
              fontSize: 9,
              textTransform: 'uppercase' as const,
              letterSpacing: '0.18em',
              fontWeight: 600,
              color: 'var(--f-accent, #1c160f)',
              opacity: 0.85,
              whiteSpace: 'nowrap',
            }}>Most booked</div>
          )}
        </div>

        {/* Selection circle */}
        <div style={{
          flexShrink: 0,
          width: 22,
          height: 22,
          borderRadius: '50%',
          border: `1.2px solid ${selected ? 'var(--f-accent, #1c160f)' : 'var(--f-hairline, rgba(28,18,12,0.10))'}`,
          background: selected ? 'var(--f-accent, #1c160f)' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 4,
          transition: 'background 240ms ease, border-color 240ms ease',
        }}>
          {selected && (
            <svg width="10" height="10" viewBox="0 0 10 10">
              <path d="M2 5L4 7L8 3" stroke="var(--f-sel-fg, #F5EDE0)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          )}
        </div>
      </div>

      {service.description && (
        <div style={{
          fontSize: 13,
          lineHeight: 1.45,
          color: 'var(--f-ink-soft, rgba(28,18,12,0.62))',
          letterSpacing: '-0.005em',
          marginTop: 2,
        }}>{service.description}</div>
      )}

      <div style={{
        marginTop: 10,
        paddingTop: 12,
        borderTop: `0.5px solid ${selected ? 'var(--f-accent-border, rgba(28,18,12,0.20))' : 'var(--f-hairline, rgba(28,18,12,0.10))'}`,
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        fontSize: 12.5,
        color: 'var(--f-ink-soft, rgba(28,18,12,0.62))',
        letterSpacing: '-0.005em',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}>
        <span>{formatDuration(service.duration)}</span>
        <span style={{ color: 'var(--f-ink-faint, rgba(28,18,12,0.40))' }}>·</span>
        <span style={{ color: 'var(--f-ink, #1c160f)', fontWeight: 500 }}>${service.price}</span>
      </div>
    </button>
  );
}

export default function ServiceSelectionStep({
  selectedService,
  onSelectService,
  services,
}: ServiceSelectionStepProps) {
  const displayServices = services ?? [];

  if (displayServices.length === 0) {
    return (
      <p style={{
        fontSize: 14,
        color: 'var(--f-ink-soft, rgba(28,18,12,0.62))',
        padding: '16px 0',
        margin: 0,
      }}>
        No services available.
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {displayServices.map((service) => (
        <ServiceCard
          key={service.id}
          service={service}
          selected={selectedService?.id === service.id}
          onClick={() => onSelectService(service)}
        />
      ))}
    </div>
  );
}
